import { categoryDisplayName, configItemsForCategory } from '../utils/guidedCategories';
import { ClientHome } from './ClientHome';
import { isClientRoleName } from '../utils/clientRoles';
import { getActiveViewRole } from '../utils/roleUtils';
import { TopNavigation } from './TopNavigation';
import { AdaptiveOnboarding } from './AdaptiveOnboarding';
import { GuidedMode } from './GuidedMode';
import { useState, useEffect } from 'react';
import { projectId } from '../utils/supabase/info';
import type { HierarchicalConfigItem, OnboardingState } from '../types/phase1e';
import { toast } from 'sonner';
import { cachedFetch } from '../utils/fetchWithCache';
import { CompanyPicker, persistActiveCompany, getPersistedCompany } from './CompanyPicker';

interface ClientDashboardNewProps {
  accessToken: string;
  currentUser: any;
  companyId: string;
  companyName: string;
  onLogout: () => void;
}

type ViewMode = 'onboarding' | 'guided' | 'plan';

export function ClientDashboardNew({  
  accessToken, 
  currentUser, 
  companyId: propCompanyId, 
  companyName: propCompanyName,
  onLogout 
}: ClientDashboardNewProps) {
  // ─── Resolved company context (prop → localStorage → picker) ───
  const [resolvedCompanyId, setResolvedCompanyId] = useState<string>(() => {
    if (propCompanyId) return propCompanyId;
    // Try to restore from localStorage
    const persisted = getPersistedCompany();
    if (persisted?.id) {
      console.log('[ClientDashboard] Restored company from localStorage:', persisted.id, persisted.name);
      return persisted.id;
    }
    return '';
  });
  const [resolvedCompanyName, setResolvedCompanyName] = useState<string>(() => {
    if (propCompanyId && propCompanyName) return propCompanyName;
    const persisted = getPersistedCompany();
    return persisted?.name || propCompanyName || 'Your Company';
  });

  // Sync if prop changes (e.g. parent resolved it later)
  useEffect(() => {
    if (propCompanyId && propCompanyId !== resolvedCompanyId) {
      setResolvedCompanyId(propCompanyId);
      if (propCompanyName) setResolvedCompanyName(propCompanyName);
      persistActiveCompany(propCompanyId, propCompanyName || 'Your Company');
    }
  }, [propCompanyId, propCompanyName]);

  // Use resolved values everywhere below
  const companyId = resolvedCompanyId;
  const companyName = resolvedCompanyName;

  const handleCompanySelected = (id: string, name: string) => {
    console.log('[ClientDashboard] Company selected from picker:', id, name);
    setResolvedCompanyId(id);
    setResolvedCompanyName(name);
    persistActiveCompany(id, name);
    // Reset loading state so bundle fetch triggers
    setLoading(true);
  };

  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('plan');
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [configItems, setConfigItems] = useState<HierarchicalConfigItem[]>([]);
  
  // Prevents the infinite loop: bundle says needsOnboarding=true -> 
  // AdaptiveOnboarding says "already complete" -> onComplete fires ->
  // loadDashboardBundle -> needsOnboarding=true again.  Once the user
  // (or AdaptiveOnboarding) has dismissed onboarding, never go back.
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  
  // Guided mode state
  const [guidedCategory, setGuidedCategory] = useState<string | null>(null);
  const [guidedCategoryName, setGuidedCategoryName] = useState<string>('');
  
  // Client roles (and a system admin viewing as one) get the client home; anyone else gets the
  // same home read-only, as a preview of what the client sees (RESTORE_PLAN R2.6). The client
  // side has one workspace, so there is no switcher to resolve.
  const isClientView = isClientRoleName(getActiveViewRole(currentUser));


  // Bundle data for child components (loaded once, passed as props)
  const [bundleData, setBundleData] = useState<any>(null);

  useEffect(() => {
    if (!companyId) {
      console.log('[ClientDashboard] No companyId provided');
      setLoading(false);
      return;
    }
    loadDashboardBundle();
  }, [companyId]);

  /**
   * SINGLE combined fetch -- replaces 5-7 sequential HTTP calls with 1.
   * Server returns: onboarding state, pre-filled data, config items,
   * company data, and recommendation engine output.
   */
  const loadDashboardBundle = async () => {
    const fetchStart = Date.now();
    setLoading(true);
    try {
      const response = await cachedFetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/client/dashboard-bundle/${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const bundle = await response.json();
        const frontendTotal = Date.now() - fetchStart;

        // Store full bundle for child components
        setBundleData(bundle);

        // Set onboarding state
        if (bundle.onboardingState) {
          setOnboardingState(bundle.onboardingState);
        }

        // Set config items
        const items: HierarchicalConfigItem[] = bundle.configItems || [];
        setConfigItems(items);

        // Set view mode based on onboarding status
        // IMPORTANT: once onboarding has been dismissed (by AdaptiveOnboarding
        // calling onComplete), never re-enter onboarding mode -- this prevents
        // the infinite oscillation loop.
        if (bundle.needsOnboarding && !onboardingDismissed) {
          setViewMode('onboarding');
        } else {
          setViewMode('plan');
        }

        console.log(`[ClientDashboard] Bundle loaded in ${bundle._elapsed}ms (frontend: ${frontendTotal}ms): ${items.length} CIs, onboarding=${bundle.needsOnboarding}, cached=${bundle._cached || false}`);
        if (bundle._timings) {
          console.log('[ClientDashboard] Server timings:', bundle._timings);
        }
      } else {
        // Fallback: individual calls if bundle endpoint unavailable
        console.warn('[ClientDashboard] Bundle endpoint failed, falling back to legacy calls');
        await loadDashboardLegacy();
      }
    } catch (error) {
      console.error('[ClientDashboard] Bundle error, trying legacy:', error);
      await loadDashboardLegacy();
    } finally {
      setLoading(false);
    }
  };

  /** Legacy fallback: parallel individual calls */
  const loadDashboardLegacy = async () => {
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const base = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

      // Fire both in parallel (was sequential before)
      const [onboardingRes, ciRes] = await Promise.all([
        cachedFetch(`${base}/company-data/${companyId}/onboarding-state`, { headers }).catch(() => null),
        cachedFetch(`${base}/config-items/${companyId}`, { headers }).catch(() => null),
      ]);

      let needsOnboarding = false;
      if (onboardingRes?.ok) {
        const state: OnboardingState = await onboardingRes.json();
        setOnboardingState(state);
        const allStepsComplete = Object.values(state.steps).every(step => step.completed);
        needsOnboarding = !allStepsComplete;
      } else {
        needsOnboarding = true;
      }

      if (ciRes?.ok) {
        const data = await ciRes.json();
        setConfigItems(data.items || []);
      }

      setViewMode(needsOnboarding && !onboardingDismissed ? 'onboarding' : 'plan');
    } catch (error) {
      console.error('[ClientDashboard] Legacy load error:', error);
      toast.error('Failed to load dashboard');
    }
  };

  const loadConfigItems = async () => {
    // Only reload config items + recommendations -- do NOT re-evaluate
    // onboarding (that would cause the infinite loop).
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const base = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;
      const ciRes = await cachedFetch(`${base}/config-items/${companyId}`, { headers });
      if (ciRes?.ok) {
        const data = await ciRes.json();
        setConfigItems(data.items || []);
      }
    } catch (error) {
      console.error('[ClientDashboard] Error reloading config items:', error);
    }
  };

  const handleOnboardingComplete = () => {
    setOnboardingDismissed(true);
    setViewMode('plan');
    loadConfigItems();
  };

  const handleStartCategory = (category: string) => {
    setGuidedCategory(category);
    setGuidedCategoryName(categoryDisplayName(category));
    setViewMode('guided');
  };

  const handleGuidedComplete = () => {
    setGuidedCategory(null);
    setGuidedCategoryName('');
    setViewMode('plan');
    loadConfigItems(); // Reload to show updated progress
  };

  const handleGuidedExit = () => {
    setGuidedCategory(null);
    setGuidedCategoryName('');
    setViewMode('plan');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Guard: no companyId resolved (e.g. role switch to client without a linked company)
  if (!companyId) {
    return (
      <CompanyPicker
        accessToken={accessToken}
        currentUser={currentUser}
        onSelect={handleCompanySelected}
        onSwitchRole={() => {
          // Navigate to system-admin or reload to trigger role switch UI
          window.history.pushState({}, '', '/system-admin');
          window.location.reload();
        }}
        onLogout={onLogout}
      />
    );
  }

  // Onboarding view (takes priority over workspace routing)
  if (viewMode === 'onboarding') {
    return (
      <AdaptiveOnboarding
        companyId={companyId}
        companyName={companyName}
        accessToken={accessToken}
        currentUser={currentUser}
        onComplete={handleOnboardingComplete}
        initialOnboardingState={bundleData?.onboardingState || null}
        initialPreFilledData={bundleData?.preFilledData || null}
      />
    );
  }

  // Guided mode view
  if (viewMode === 'guided' && guidedCategory) {
    console.log('[ClientDashboardNew] Entering guided mode:', {
      guidedCategory,
      guidedCategoryName,
      totalConfigItems: configItems.length,
      configItemsSample: configItems.slice(0, 5).map(item => ({
        id: item.id,
        name: item.name,
        category: item.category,
        type: item.type
      }))
    });

    // The category is a config-item category (the grid's guided block and the home's row carry
    // it as the items spell it); the items are matched on it, with the alias table for a
    // category handed over in another spelling.
    const categoryItems = configItemsForCategory(configItems, guidedCategory);

    return (
      <GuidedMode
        companyId={companyId}
        category={guidedCategory}
        categoryName={guidedCategoryName}
        configItems={categoryItems}
        accessToken={accessToken}
        currentUser={currentUser}
        onComplete={handleGuidedComplete}
        onExit={handleGuidedExit}
      />
    );
  }

  // Engagement Plan View (New Default)
  if (viewMode === 'plan' && isClientView) {
    return (
      <ClientHome
        companyId={companyId}
        companyName={companyName}
        accessToken={accessToken}
        currentUser={currentUser}
        onLogout={onLogout}
        onStartCategory={handleStartCategory}
      />
    );
  }

  // Every other viewer (a consultant, a firm admin) sees the client's home as a read-only preview.
  // The engagement view that the Engagement Plan held for them is on the client page's Overview
  // (EngagementOverview). Onboarding and guided mode return earlier.
  return (
    <ClientHome
      companyId={companyId}
      companyName={companyName}
      accessToken={accessToken}
      currentUser={currentUser}
      onLogout={onLogout}
      onStartCategory={handleStartCategory}
      readOnly={!isClientView}
    />
  );
}
